import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMCountCategoriesCard,
  PMDonutChartCard,
} from "@pimo/pimo-components/src/lib/pm-components";

import { TestAppState } from "../../../app";
import { APP_ROUTES } from "../../../constants";
import { getDropdownCount } from "../../../utils/dropdown-options";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../../utils/get-plural-of-project-name";
import { getOverviewCardCopy } from "../get-card-copy";

export function addProjectStatusDonutAndCountCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): void {
  const {
    language,
    itemName,
    itemNamePlural,
    projectName,
    projectNamePlural,
    getCardCopy,
  } = getOverviewCardCopy(app);

  const statusOfProjectsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "statusOfProjectsDonutChartComponent",
      title: "",
      description: "",
      labels: ["donutChart", "chart", "status"],
    },
  });

  statusOfProjectsDonutChartComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "statusOfProjectsDonutChartComponent",
      labels: ["donutChart", "chart", "status"],
      title: getCardCopy(language, "statusOfProjects").title,
      description: getCardCopy(language, "statusOfProjects").description,
    };
  });

  statusOfProjectsDonutChartComponent.mapState(
    ({ dashboard, settings, language }) => {
      const statusCounts = dashboard?._calculated?.projectStatusCounts ?? {};
      const data = (settings?.configuration?.statusDefinition ?? []).map(
        (entry) => {
          return {
            label: entry.name ?? "",
            value: getDropdownCount(statusCounts, entry),
            color: entry.color ?? "",
          };
        }
      );

      const handleStatusFilter = (status?: string) => {
        app?.patchAppState({
          filterData: {
            searchQueryFilter: [],
            statusFilter: status ? [status] : [],
            completionFilter: [],
            categoryFilter: [],
            updateStatusFilter: [],
          },
        });
        app?.navigate(APP_ROUTES.overview);
      };

      return {
        title: getLocalizedCardCopy(language, "statusOfProjects", {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            settings?.configuration?.projectName,
            language
          ),
          projectNamePlural: getPluralOfProjectName(
            settings?.configuration?.projectName,
            language
          ),
        }).title,
        icon: "DonutLarge",
        data,
        onCardClick: () => handleStatusFilter(),
        onSegmentClick: (segmentIndex: number) =>
          handleStatusFilter(data[segmentIndex]?.label),
      };
    }
  );

  statusOfProjectsDonutChartComponent.mapVisibility(({ settings }) => {
    return (
      (settings?.configuration?.statusReportingLevel ?? "overall") === "overall"
    );
  });

  const countofProjectStatusComponent = view.addComponent({
    component: PMCountCategoriesCard,
    layoutProps: {
      componentId: "countofProjectStatusComponent",
      labels: ["status", "count"],
      description: getCardCopy(language, "projectStatusCounts").description,
      title: getCardCopy(language, "projectStatusCounts").title,
    },
  });

  countofProjectStatusComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "countofProjectStatusComponent",
      labels: ["status", "count"],
      title: getCardCopy(language, "projectStatusCounts").title,
      description: getCardCopy(language, "projectStatusCounts").description,
    };
  });

  countofProjectStatusComponent.mapState(
    ({ dashboard, settings, language }) => {
      const statusCounts = dashboard?._calculated?.projectStatusCounts ?? {};
      const data = (settings?.configuration?.statusDefinition ?? []).map(
        (entry) => {
          return {
            label: entry.name ?? "",
            count: getDropdownCount(statusCounts, entry),
            color: entry.color ?? "",
          };
        }
      );

      return {
        title: getLocalizedCardCopy(language, "projectStatusCounts", {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            settings?.configuration?.projectName,
            language
          ),
          projectNamePlural: getPluralOfProjectName(
            settings?.configuration?.projectName,
            language
          ),
        }).title,

        data,
        showInfoIcon: false,
        showMenuIcon: false,
      };
    }
  );

  const countofItemsStatusComponent = view.addComponent({
    component: PMCountCategoriesCard,
    layoutProps: {
      componentId: "countofItemsStatusComponent",
      labels: ["status", "count"],
      description: getCardCopy(language, "itemStatusCounts").description,
      title: getCardCopy(language, "itemStatusCounts").title,
    },
  });

  countofItemsStatusComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "countofItemsStatusComponent",
      labels: ["status", "count"],
      title: getCardCopy(language, "itemStatusCounts").title,
      description: getCardCopy(language, "itemStatusCounts").description,
    };
  });

  countofItemsStatusComponent.mapState(({ dashboard, settings, language }) => {
    const statusCounts = dashboard?._calculated?.statusCounts ?? {};
    const data = (settings?.configuration?.statusDefinition ?? []).map(
      (entry) => {
        return {
          label: entry.name ?? "",
          count: getDropdownCount(statusCounts, entry),
          color: entry.color ?? "",
        };
      }
    );

    return {
      title: getLocalizedCardCopy(language, "itemStatusCounts", {
        itemName: getItemName(settings?.configuration?.itemName, language),
        itemNamePlural: getPluralOfItemName(
          settings?.configuration?.itemName,
          language
        ),
        projectName,
        projectNamePlural,
      }).title,
      icon: "DonutLarge",
      data,
    };
  });
}
